import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { LIMITES } from '../limites';
import { Brain, Check, ChevronRight, Clock, Play, Settings, Users, X, LoaderCircle, Star, Award, Database, Bot } from 'lucide-react';
import './Basta.css';

// --- Configuración Principal del Juego ---
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split('');
const DICE_ROLL_DURATION = 2000;
const TIME_PER_CATEGORY = 12;
const ANIMATION_DELAY_PER_CATEGORY = 300; // 300ms por categoría

const DIFFICULTY_SETTINGS = {
  easy: { name: 'Easy', baseTime: 180, maxCategories: 5 }, // 3 minutes
  normal: { name: 'Normal', baseTime: 300, maxCategories: 7 }, // 5 minutes
  hard: { name: 'Hard', baseTime: 420, maxCategories: 10 }, // 7 minutes
};

// Mapeo temporal de categorías en español a inglés (ajusta según tus datos reales)
const CATEGORY_TRANSLATIONS = {
  'Animales': 'Animals',
  'Frutas': 'Fruits',
  'Colores': 'Colors',
  'Países': 'Countries',
  'Oficios': 'Professions',
  'Deportes': 'Sports',
  'Comida': 'Food',
  'Ciudades': 'Cities',
  'Profesiones': 'Professions',
  'Cosas': 'things',
  'Verbos': 'Verbs',
  'Ropa': 'Clothing',
  'Naturaleza': 'Nature',

  // Añade más según tus categorías
};

const Dice = ({ letter, isRolling }) => (
  <div className="bs-dice-container">
    <div className={`bs-dice ${isRolling ? 'rolling' : ''}`}>
      <div className="bs-face front">{letter}</div>
      <div className="bs-face back">?</div>
      <div className="bs-face right">B</div>
      <div className="bs-face left">S</div>
      <div className="bs-face top">T</div>
      <div className="bs-face bottom">A</div>
    </div>
  </div>
);

function Basta() {
  // --- Game States ---
  const [gameState, setGameState] = useState('menu');
  const [difficulty, setDifficulty] = useState('normal');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [currentLetter, setCurrentLetter] = useState('?');
  const [isRolling, setIsRolling] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => parseInt(localStorage.getItem('bastaHighScore') || '0', 10));
  const [roundResults, setRoundResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const settings = DIFFICULTY_SETTINGS[difficulty];
  const totalGameTime = useMemo(() => settings.baseTime + (selectedCategories.length * TIME_PER_CATEGORY), [settings.baseTime, selectedCategories.length]);

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      try {
        // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
        const response = await fetch('/api/minigames/get-basta-categories', { 
          headers: { 'Authorization': `Bearer ${token}` } 
        });
        if (!response.ok) throw new Error('Failed to load categories.');
        const data = await response.json();
        // Traduce las categorías al inglés usando el mapeo
        const translatedCategories = data.map(cat => ({
          ...cat,
          name: CATEGORY_TRANSLATIONS[cat.name] || cat.name, // Usa la traducción o el nombre original si no está mapeado
        }));
        setCategories(translatedCategories);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleBastaClick = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setGameState('submitting');
    const token = localStorage.getItem('token');

    try {
      // 🔥 CAMBIO CRÍTICO: URL RELATIVA en lugar de absoluta
      const response = await fetch('/api/minigames/validate-basta-answers', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          letter: currentLetter, 
          answers 
        }),
      });
      if (!response.ok) throw new Error('Error validating answers.');
      const resultsData = await response.json();
      setRoundResults(resultsData);

      const roundScore = Object.values(resultsData).reduce((total, result) => total + result.points, 0);
      const correctAnswersCount = Object.values(resultsData).filter(r => r.is_correct).length;
      const newTotalScore = score + roundScore;
      setScore(newTotalScore);

      // Guardar XP en el backend
      if (correctAnswersCount > 0) {
        try {
          await fetch('/api/minigames/save-score', {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                  game_name: 'basta',
                  score: roundScore,
                  correct_answers: correctAnswersCount,
                  total_questions: selectedCategories.length
              })
          });
        } catch (error) {
            console.error("Error guardando XP:", error);
        }
      }

      if (newTotalScore > highScore) {
        setHighScore(newTotalScore);
        localStorage.setItem('bastaHighScore', newTotalScore.toString());
      }
      setGameState('results');
    } catch (err) {
      setError(err.message);
      setGameState('menu');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, currentLetter, answers, score, highScore]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft <= 0) {
      handleBastaClick();
    }
    let timerId;
    if (gameState === 'playing') {
      timerId = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    }
    return () => clearInterval(timerId);
  }, [gameState, timeLeft, handleBastaClick]);

  const handleStartRoll = () => {
    setGameState('rolling');
    setIsRolling(true);
    const rollInterval = setInterval(() => setCurrentLetter(LETTERS[Math.floor(Math.random() * LETTERS.length)]), 100);
    setTimeout(() => {
      clearInterval(rollInterval);
      setCurrentLetter(LETTERS[Math.floor(Math.random() * LETTERS.length)]);
      setIsRolling(false);
      setGameState('choosing');
    }, DICE_ROLL_DURATION);
  };

  const chooseCategories = () => {
    setSelectedCategories([]); // Clear selected categories
    const shuffled = [...categories].sort(() => 0.5 - Math.random());
    const selectedKeys = shuffled.slice(0, settings.maxCategories).map(cat => cat.key);

    // Add categories with animation and transition to playing at the end
    selectedKeys.forEach((key, index) => {
      setTimeout(() => {
        setSelectedCategories(prev => {
          const newSelected = [...prev, key];
          const element = document.querySelector(`.bs-category-card[data-key="${key}"]`);
          if (element) element.classList.add('animate-select');
          // Change to playing after the last category
          if (index === selectedKeys.length - 1) {
            setTimeout(() => {
              setTimeLeft(totalGameTime);
              setGameState('playing');
            }, ANIMATION_DELAY_PER_CATEGORY); // Wait one more cycle for animation
          }
          return newSelected;
        });
      }, index * ANIMATION_DELAY_PER_CATEGORY);
    });
  };

  useEffect(() => {
    if (gameState === 'choosing') {
      chooseCategories();
    }
  }, [gameState]);

  const handleAnswerChange = (categoryKey, value) => {
    setAnswers(prev => ({ ...prev, [categoryKey]: value }));
  };

  const handlePlayAgain = () => {
    setGameState('menu');
    setSelectedCategories([]);
    setAnswers({});
    setScore(0);
    setRoundResults(null);
    setCurrentLetter('?');
  };

  const renderMenu = () => (
    <div className="bs-menu">
      <div className="bs-header"><Brain className="h-8 w-8" /><h1>Basta Challenge</h1><p>Test your vocabulary against the clock.</p></div>
      <div className="bs-difficulty-selector">{Object.entries(DIFFICULTY_SETTINGS).map(([key, value]) => (<button key={key} className={`bs-difficulty-btn ${difficulty === key ? 'active' : ''}`} onClick={() => setDifficulty(key)}>{value.name}</button>))}</div>
      <div className="bs-difficulty-details"><div className="bs-detail-item"><Clock size={16} /><span>{settings.baseTime}s base</span></div><div className="bs-detail-item"><Users size={16} /><span>Up to {settings.maxCategories} categories</span></div><div className="bs-detail-item"><Settings size={16} /><span>+{TIME_PER_CATEGORY}s per category</span></div></div>
      <button className="bs-play-btn" onClick={handleStartRoll}><Play />Start</button>
    </div>
  );

  const renderRolling = () => (<div className="bs-rolling"><h2>Rolling the die...</h2><Dice letter={currentLetter} isRolling={isRolling} /></div>);

  const renderChoosing = () => (
    <div className="bs-choosing">
      <h2>Choosing categories...</h2>
      <div className="bs-category-preview">
        {categories.map(cat => (
          <div key={cat.key} className={`bs-category-card ${selectedCategories.includes(cat.key) ? 'selected' : ''}`} data-key={cat.key}>
            <div className="bs-category-icon">{cat.icon}</div>
            <div className="bs-category-name">{cat.name}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPlaying = () => (
    <div className="bs-playing">
      <div className="bs-playing-header">
        <div className="bs-playing-letter">{currentLetter}</div>
        <div className="bs-playing-timer">{timeLeft}s</div>
        <button className="bs-stop-btn" onClick={handleBastaClick} disabled={isSubmitting}>
          {isSubmitting ? 'Scoring...' : 'STOP!'}
        </button>
      </div>
      <div className="bs-answers-grid">
        {selectedCategories.map(key => {
          const category = categories.find(c => c.key === key) || { icon: '❓', name: 'Unknown' };
          return (
            <div key={key} className="bs-answer-field">
              <label><span className="bs-answer-icon">{category.icon}</span>{category.name}</label>
              <input type="text" placeholder={`Word with "${currentLetter}"...`} value={answers[key] || ''} onChange={(e) => handleAnswerChange(key, e.target.value)} />
                  maxLength={LIMITES.respuestaMinijuego}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderResults = () => {
    if (!roundResults) return <div className="bs-loading"><LoaderCircle className="animate-spin h-10 w-10" />Loading results...</div>;
    const roundScore = Object.values(roundResults).reduce((total, result) => total + result.points, 0);

    const getResultIcon = (source) => {
      if (source === 'db') return <Database size={14} title="Validated by Database" />;
      if (source === 'ai') return <Bot size={14} title="Validated by AI" />;
      return null;
    };

    return (
      <div className="bs-results">
        <div className="bs-results-header">
          <h2>Round Results</h2>
          <div className="bs-results-scores">
            <div><span>Round Score</span><strong>+{roundScore}</strong></div>
            <div><span>Total Score</span><strong>{score}</strong></div>
            <div><span>High Score</span><strong>{highScore}</strong></div>
          </div>
        </div>
        <div className="bs-results-grid">
          {Object.entries(roundResults).map(([key, result]) => {
            const category = categories.find(c => c.key === key) || { icon: '❓', name: 'Unknown' };
            return (
              <div key={key} className={`bs-result-card ${result.is_correct ? 'Excellent' : 'Incorrect'}`}>
                <div className="bs-result-card-header">
                  <div className="bs-result-category">
                    <span className="bs-answer-icon">{category.icon}</span>
                    {category.name}
                  </div>
                  <div className={`bs-result-points`}>
                    {result.is_correct ? `+${result.points}` : '+0'}
                  </div>
                </div>
                <div className="bs-result-word">{result.word || <span className="placeholder-text">(empty)</span>}</div>
                <div className="bs-result-reason">
                  {result.is_correct ? <Check size={14} /> : <X size={14} />}
                  <span>{result.reason || (result.is_correct ? 'Correcta' : `La palabra '${result.word || '...'}' no es válida para esta categoría.`)}</span>
                  {getResultIcon(result.source)}
                </div>
              </div>
            );
          })}
        </div>
        <button className="bs-play-btn" onClick={handlePlayAgain}><Play />Play Again</button>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) return <div className="bs-loading"><LoaderCircle className="animate-spin h-10 w-10" />Loading...</div>;
    if (error) return <div className="bs-error">{error}</div>;
    if (gameState === 'submitting') return <div className="bs-loading"><LoaderCircle className="animate-spin h-10 w-10" />Scoring answers...</div>;

    switch (gameState) {
      case 'menu': return renderMenu();
      case 'rolling': return renderRolling();
      case 'choosing': return renderChoosing();
      case 'playing': return renderPlaying();
      case 'results': return renderResults();
      default: return renderMenu();
    }
  };

  return (<div className="bs-container"><div className="bs-inner-container">{renderContent()}</div></div>);
}

export default Basta;